'use client';

/**
 * HomeRecentCard — recent activity feed.
 *
 * Reads `recent_activity` strategy. Each item click opens an entity
 * tab using the item's actual `entity_type` (rfq / project / vendor /
 * …) — `useTabStore.openTab` then runs mode-promotion through
 * `getModeForEntityType` and lands the user on the mode that best
 * handles that entity (e.g. rfq → mode1, vendor → mode4). Items
 * without an `entity_type` fall back to `'rfq'` (preserves the legacy
 * behaviour for typeless items). Layer-2 translation respected
 * (`meta_translated` preferred when set).
 *
 * @see klyde_edge/composition/cards/home.py — content_key="home_recent"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Home
 * @layer: presentation
 */

import { Clock, MessageSquare, ChevronRight } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { text, ink, faint, surfaceBase } from '@/lib/design/tokens';
import { CardShell } from '../card-shell';
import { cn } from '@/lib/utils/cn';
import { useAffordance } from '@/lib/stores/total-matrix-store';

interface RecentActivityResponse {
  items: {
    id: string;
    title: string;
    meta: string;
    meta_translated: string | null;
    translated_to: string | null;
    language: string | null;
    entity_name: string | null;
    entity_type: string | null;
    entity_id: string;
    // Source entity — the original target of the message (RQ_* for RFQ
    // messages even when the route entity is the parent project).
    // Drives the one-shot focus signal in the destination view.
    source_entity_id: string | null;
    source_entity_type: string | null;
    time_ago: string;
  }[];
  labels: Record<string, string>;
}

export function HomeRecentCard() {
  const openTab = useTabStore((s) => s.openTab);
  const setRfqFocus = useLayoutStore((s) => s.setRfqFocus);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const lang = useLanguageStore((s) => s.lang) ?? undefined;
  const params = lang ? { lang } : {};

  // Land the user on the route entity, AND (where applicable) communicate
  // the source RFQ via the one-shot focus signal — so the kanban (or
  // whichever destination view) can scroll/highlight the specific RFQ
  // row that started this navigation. Mirrors how the home pipeline tile
  // uses pipelineStageFocus.
  const handleClick = (item: RecentActivityResponse['items'][number]) => {
    if (!item.entity_id) return;

    // If the source was an RFQ (purchase.order) but the route walks up to
    // a project, set the one-shot focus so the kanban can highlight it.
    if (
      item.source_entity_type === 'purchase.order' &&
      item.source_entity_id &&
      item.entity_type === 'project'
    ) {
      setRfqFocus(item.source_entity_id);
    }

    openTab({
      id: item.entity_id,
      type: item.entity_type ?? 'rfq',
      entityId: item.entity_id,
      title: item.entity_name ?? item.title,
    });

    // openTab clears the graph-selection store and runs mode-promotion;
    // the destination cards' projectId resolution (useObeyaProjectData)
    // reads useGraphSelectionStore.projectId, so explicitly set the
    // selection AFTER openTab — belt-and-braces for the project route.
    if (item.entity_type === 'project') {
      selectNode(
        { id: item.entity_id, label: item.entity_name ?? item.title, type: 'project' },
        item.entity_id,
      );
    }
  };

  const { data: resp, isLoading } = useStrategyData<RecentActivityResponse>('recent_activity', params);
  const data = resp?.success ? resp.data : undefined;
  const items = data?.items ?? [];
  const L = data?.labels ?? {};
  const loadingTestId = useAffordance('home.recent_activity.loading');

  return (
    <CardShell style={{ fontSize: surfaceBase.spacious }} className="overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <h3 className={text.heading}>{L.recent_title ?? 'Recent Activity'}</h3>
        </div>
        <button className={cn(text.muted, 'hover:text-foreground transition-colors cursor-pointer')}>
          {L.view_all ?? 'View all'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div data-testid={loadingTestId} className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="shrink-0 w-8 h-8 rounded-lg bg-muted animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
                  <div className="h-3 w-44 rounded bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="divide-y divide-border">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/30 transition-colors group cursor-pointer"
              >
                <div className="shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(text.title, 'truncate')}>{item.title}</p>
                  <p className={cn(text.muted, 'truncate')}>{item.meta_translated ?? item.meta}</p>
                </div>
                <span className={cn(text.muted, faint, 'shrink-0')}>{item.time_ago}</span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Clock className="h-8 w-8 text-muted-foreground/40" />
            <p className={cn(text.title, ink.muted)}>{L.no_recent_title ?? 'Nothing recent'}</p>
            <p className={cn(text.muted, faint)}>{L.no_recent_subtitle ?? 'Updates will appear here'}</p>
          </div>
        )}
      </div>
    </CardShell>
  );
}
